import { Button } from '../../components/ui/button';
import { validateBroadcastForSend } from '../../lib/validateBroadcastSend';
import type { AudiencePreviewResult } from '../../types/broadcast';
import type { ComposeDraft } from './BroadcastCompose';

// Stage 6A §5.5 — pre-send confirmation. Shows the audience size + per-channel
// reach, a sample of recipients, a large-send warning, and any send-time
// validation errors. Final Send is blocked while errors remain.

type ConfirmSendModalProps = {
  draft: ComposeDraft;
  audience: AudiencePreviewResult | undefined;
  sending: boolean;
  sendError: string | null;
  onCancel: () => void;
  onConfirm: () => void;
};

const LARGE_SEND_THRESHOLD = 100;

export function ConfirmSendModal({
  draft,
  audience,
  sending,
  sendError,
  onCancel,
  onConfirm,
}: ConfirmSendModalProps) {
  const reach = {
    emailReachable: audience?.emailReachable ?? 0,
    smsReachable: audience?.smsReachable ?? 0,
  };
  const errors = validateBroadcastForSend(draft, reach);

  const hasEmail = draft.channels.includes('email');
  const hasSms = draft.channels.includes('sms');
  const totalSends = (hasEmail ? reach.emailReachable : 0) + (hasSms ? reach.smsReachable : 0);
  const sample = audience?.recipients.slice(0, 3) ?? [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-md border border-slate-200 bg-white p-5 shadow-xl">
        <h2 className="mb-1 text-lg font-semibold">Send broadcast</h2>
        <p className="mb-4 text-sm text-slate-600">"{draft.title}"</p>

        <dl className="mb-4 grid grid-cols-3 gap-3 text-sm">
          <Stat label="Contacts" value={audience?.totalContacts ?? 0} />
          {hasEmail && <Stat label="Email" value={reach.emailReachable} />}
          {hasSms && <Stat label="SMS" value={reach.smsReachable} />}
        </dl>

        {sample.length > 0 && (
          <p className="mb-3 text-xs text-slate-500">
            e.g. {sample.map((r) => r.contactName).join(', ')}
            {audience && audience.totalContacts > sample.length ? ', …' : ''}
          </p>
        )}

        {totalSends > LARGE_SEND_THRESHOLD && (
          <div className="mb-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            This will send {totalSends} messages. Please confirm.
          </div>
        )}

        {errors.length > 0 && (
          <ul className="mb-3 list-inside list-disc rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {errors.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        )}

        {sendError && <p className="mb-3 text-sm text-red-600">{sendError}</p>}

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onCancel} disabled={sending}>
            Cancel
          </Button>
          <Button onClick={onConfirm} disabled={sending || errors.length > 0}>
            {sending ? 'Sending…' : `Send to ${totalSends}`}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded border border-slate-200 px-3 py-2 text-center">
      <dd className="text-xl font-semibold text-slate-900">{value}</dd>
      <dt className="text-xs text-slate-500">{label}</dt>
    </div>
  );
}
